import React, { useEffect, useState } from 'react';
import { Icon, EmptyState } from '../../../components/ui';
import { fetchCalls, fetchRecordingUrl } from '../api';
import { CallLogRow } from '../types';
import { callMeta, isMissed, fmtCallDuration, fmtCallTime } from '../callMeta';
import { CallButton } from './CallButton';

/**
 * CallHistory — call log list for a customer or deal card. Shows
 * direction, outcome, who handled it, duration, and a recording
 * player when available. Missed calls get a one-tap call-back.
 *
 *   <CallHistory customerId={id} />   or   <CallHistory dealId={id} />
 */

const fmtDur = (s: number) => (s ? `${fmtCallDuration(s)} דק׳` : '');

export const CallHistory: React.FC<{ customerId?: string; dealId?: string }> = ({ customerId, dealId }) => {
  const [rows, setRows] = useState<CallLogRow[] | null>(null);

  useEffect(() => {
    let alive = true;
    fetchCalls({ customerId, dealId })
      .then(r => alive && setRows(r.calls))
      .catch(() => alive && setRows([]));
    return () => { alive = false; };
  }, [customerId, dealId]);

  if (rows === null) {
    return (
      <div className="py-8 text-center text-sm text-slate-400">
        <Icon name="progress_activity" size={20} className="animate-spin mx-auto mb-2" /> טוען שיחות…
      </div>
    );
  }
  if (!rows.length) {
    return <EmptyState icon="call" title="אין שיחות" description="שיחות עם הלקוח יופיעו כאן אוטומטית." />;
  }

  return (
    <div className="divide-y divide-slate-100 dark:divide-slate-800">
      {rows.map(c => {
        const meta = callMeta(c);
        const missed = isMissed(c);
        return (
          <div key={c.id} className="flex items-center gap-3 py-2.5">
            <Icon name={meta.icon} size={20} className={`shrink-0 ${meta.color}`} />
            <div className="min-w-0">
              <div className="font-semibold text-sm">{meta.label}</div>
              <div className="text-xs text-slate-500 truncate">
                {fmtCallTime(c.startedAt)}
                {c.durationSec ? ` · ${fmtDur(c.durationSec)}` : ''}
                {c.user ? ` · ${c.user.name}` : ''}
              </div>
            </div>
            {(c.hasRecording || missed) && (
              <div className="ms-3 shrink-0">
                {c.hasRecording ? <RecordingPlayer callId={c.id} /> : <CallButton number={c.number} />}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/**
 * RecordingPlayer — plays a private call recording. Fetches a signed URL
 * on first play (never exposes the object key) and shows an inline audio
 * element once loaded.
 */
export const RecordingPlayer: React.FC<{ callId: string }> = ({ callId }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (url || loading) return;
    setLoading(true);
    const u = await fetchRecordingUrl(callId);
    setUrl(u);
    setLoading(false);
  };

  if (url) {
    return <audio src={url} controls autoPlay className="h-8 w-[168px] align-middle" />;
  }
  return (
    <button
      onClick={load}
      disabled={loading}
      title="האזן להקלטה"
      className="size-8 rounded-lg bg-slate-100 dark:bg-slate-800 text-primary flex items-center justify-center hover:bg-slate-200 disabled:opacity-50"
    >
      <Icon name={loading ? 'progress_activity' : 'play_arrow'} size={18} className={loading ? 'animate-spin' : ''} />
    </button>
  );
};
